import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { 
  ShoppingCart, Package, CreditCard, Settings, Trash2, Plus, Minus, 
  CheckCircle, X, Search, ChevronLeft, Image as ImageIcon,
  Box, Edit3, Save, PlusCircle, CloudLightning, AlertCircle, Wifi, WifiOff,
  Activity, ExternalLink, Lock, Copy, Play, RefreshCw, Code, Database, FileText,
  LayoutDashboard, Users, User, MapPin, Phone, Receipt, FileInput, Store, Truck, Printer, Scan, QrCode, Home, Menu, Calendar, Trophy, TrendingUp, Tag, Percent, PenLine, DollarSign, Edit, UserPlus, Trash, Map, Key, Wallet, ShieldCheck, Banknote, Camera, Usb, Barcode, Volume2, Bluetooth, XCircle, Upload, FileImage, Loader
} from 'lucide-react';

// --- CONFIGURATION ---
const API_CONFIG = {
  baseUrl: "https://partner.packhai-api-88.com", 
  endpoints: {
    createOrder: "/Order/new-order",
    checkStock: "/Stock/get-stock-list",
    getWarehouses: "/Stock/get-warehouse-list"
  }
};

// ** ระบบตรวจจับอัตโนมัติ (Auto-Detect) **
// ถ้าเปิดในคอมตัวเอง (Localhost) -> ยิงไป Server จริง
// ถ้าเปิดบนเว็บจริง -> ใช้ Relative Path (เสถียรสุด)
const isLocal = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
const SERVER_BASE = isLocal ? "https://pos.phn.ssplatform.com" : ""; 

const PROXY_URL = `${SERVER_BASE}/proxy.php`; 
const UPLOAD_URL = `${SERVER_BASE}/upload.php`; 
const DB_API_URL = `${SERVER_BASE}/api.php`;

const SETTINGS_PIN = "PH123456";
const FINANCE_PIN = "PH6516542";

// --- UTILS ---
const calculateItemPrice = (item) => {
    let finalPrice = item.price; 
    if (item.discount && item.discount > 0) {
        if (item.discountType === 'PERCENT') {
            finalPrice = item.price - (item.price * item.discount / 100);
        } else {
            finalPrice = item.price - item.discount;
        }
    }
    return Math.max(0, finalPrice);
};

// Improved Sound Effects
const beep = (isError = false) => {
    try {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        if (!AudioContext) return;
        const ctx = new AudioContext();
        const oscillator = ctx.createOscillator();
        const gainNode = ctx.createGain();
        
        // Error = Sawtooth (Buzz), Success = Sine (Beep)
        oscillator.type = isError ? 'sawtooth' : 'sine';
        
        if (isError) {
             // Low pitch for error
             oscillator.frequency.setValueAtTime(150, ctx.currentTime);
             oscillator.frequency.linearRampToValueAtTime(100, ctx.currentTime + 0.3);
        } else {
             // High pitch for success scan
             oscillator.frequency.setValueAtTime(800, ctx.currentTime);
             oscillator.frequency.exponentialRampToValueAtTime(1200, ctx.currentTime + 0.1);
        }
        
        gainNode.gain.setValueAtTime(0.1, ctx.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + (isError ? 0.5 : 0.1));
        
        oscillator.connect(gainNode);
        gainNode.connect(ctx.destination);
        oscillator.start();
        oscillator.stop(ctx.currentTime + (isError ? 0.5 : 0.1));
    } catch (e) {}
};

const speakAmount = (amount) => {
    if ('speechSynthesis' in window) {
        window.speechSynthesis.cancel();
        const text = `ได้รับยอดเงิน ${amount.toLocaleString()} บาท เรียบร้อยแล้วค่ะ`;
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.lang = 'th-TH'; 
        utterance.rate = 1.0;
        utterance.volume = 1.0;
        window.speechSynthesis.speak(utterance);
    }
};

const getPromptPayUrl = (amount, number) => {
    const targetNumber = number || '0000000000';
    const cleanId = targetNumber.replace(/[^0-9]/g, '');
    return `https://promptpay.io/${cleanId}/${amount}`;
};

// --- SUB-COMPONENTS ---
const StatCard = ({ title, value, icon, color, subValue }) => (
  <div className={`p-4 md:p-6 rounded-2xl border ${color} flex items-center justify-between shadow-sm bg-white`}>
    <div>
      <p className="text-xs md:text-sm text-gray-500 mb-1">{title}</p>
      <h3 className="text-xl md:text-2xl font-bold text-gray-800">{value}</h3>
      {subValue && <p className="text-[10px] text-gray-400 mt-1">{subValue}</p>}
    </div>
    <div className={`p-3 rounded-xl ${color.replace('border', 'bg').replace('-200', '-50')}`}>{icon}</div>
  </div>
);

const NavButton = ({ id, icon, label, active, onClick }) => (
  <button
    onClick={() => onClick(id)}
    className={`p-2 md:p-3 rounded-xl transition-all flex flex-col items-center gap-1 flex-1 md:flex-none md:w-full ${active ? 'bg-blue-50 text-blue-700 shadow-sm' : 'text-gray-400 hover:bg-gray-50'}`}
  >
    {icon}
    <span className="text-[10px] font-medium">{label}</span>
  </button>
);

// --- MODALS ---
const CameraScannerModal = ({ isOpen, onClose, onScan }) => {
    const videoRef = useRef(null);
    const [hasPermission, setHasPermission] = useState(null);
    const [manualCode, setManualCode] = useState('');
    const streamRef = useRef(null);
    const [isStabilizing, setIsStabilizing] = useState(true);

    useEffect(() => {
        let detectLoop = null;
        const stopTracks = () => {
            if (streamRef.current) {
                streamRef.current.getTracks().forEach(track => track.stop());
                streamRef.current = null;
            }
            if (detectLoop) clearInterval(detectLoop);
        };

        if (isOpen) {
            setIsStabilizing(true);
            const startCamera = async () => {
                if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
                    setHasPermission(false);
                    return;
                }
                try {
                    let stream = null;
                    try {
                        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } } });
                    } catch (e) {
                        try { stream = await navigator.mediaDevices.getUserMedia({ video: true }); } catch (e2) { setHasPermission(false); return; }
                    }
                    streamRef.current = stream;
                    if (videoRef.current) {
                        videoRef.current.srcObject = stream;
                        videoRef.current.onloadedmetadata = async () => {
                            try { await videoRef.current.play(); setHasPermission(true); setTimeout(() => setIsStabilizing(false), 1000); } catch (e) { setHasPermission(false); }
                        };
                    }
                    if ('BarcodeDetector' in window) {
                        const barcodeDetector = new window.BarcodeDetector({ formats: ['qr_code', 'ean_13', 'code_128', 'code_39'] });
                        detectLoop = setInterval(async () => {
                            if (isStabilizing) return;
                            if (videoRef.current && videoRef.current.readyState === 4) {
                                try {
                                    const barcodes = await barcodeDetector.detect(videoRef.current);
                                    if (barcodes.length > 0) { onScan(barcodes[0].rawValue); onClose(); }
                                } catch (e) {}
                            }
                        }, 300);
                    }
                } catch (err) { setHasPermission(false); }
            };
            startCamera();
        }
        return () => stopTracks();
    }, [isOpen, onScan, onClose, isStabilizing]);

    if (!isOpen) return null;
    return (
        <div className="fixed inset-0 z-[90] bg-black flex flex-col items-center justify-center animate-fade-in">
            <div className="absolute top-4 right-4 z-50"><button onClick={onClose} className="bg-white/20 text-white p-2 rounded-full"><X size={24}/></button></div>
            <div className="relative w-full max-w-md h-[60vh] bg-gray-900 overflow-hidden flex items-center justify-center">
                {hasPermission === false ? (
                    <div className="text-white text-center p-4"><Camera size={48} className="mx-auto mb-4 opacity-50"/><p>Camera Unavailable</p></div>
                ) : (
                    <><video ref={videoRef} playsInline muted className="w-full h-full object-cover opacity-80"></video><div className="absolute inset-0 flex items-center justify-center"><div className={`w-64 h-40 border-2 rounded-lg relative ${isStabilizing ? 'border-yellow-500' : 'border-red-500 animate-pulse'}`}></div></div></>
                )}
            </div>
            <div className="w-full max-w-md bg-white p-6 rounded-t-3xl mt-[-20px] relative z-10 flex-1">
                <div className="flex gap-2"><input className="flex-1 p-4 border-2 rounded-xl text-xl" placeholder="Or type code..." value={manualCode} onChange={e => setManualCode(e.target.value)} onKeyDown={e => {if(e.key==='Enter'){onScan(manualCode); onClose();}}} autoFocus/><button onClick={() => {onScan(manualCode); onClose();}} className="bg-blue-700 text-white px-6 rounded-xl font-bold">OK</button></div>
            </div>
        </div>
    );
};

const PinModal = ({ isOpen, onClose, onSuccess, targetPin, title }) => {
    const [inputPin, setInputPin] = useState('');
    const [error, setError] = useState(false);
    const inputRef = useRef(null);
    useEffect(() => { if(isOpen) { setInputPin(''); setError(false); setTimeout(() => inputRef.current?.focus(), 100); } }, [isOpen]);
    const handleSubmit = (e) => { e?.preventDefault(); if (inputPin === targetPin) { onSuccess(); onClose(); } else { setError(true); setInputPin(''); } };
    if (!isOpen) return null;
    return (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-xs overflow-hidden">
                <div className="bg-blue-700 p-6 text-white text-center relative"><button onClick={onClose} className="absolute top-4 right-4"><X size={20}/></button><Lock size={40} className="mx-auto mb-2"/><h3 className="text-lg font-bold">{title}</h3></div>
                <div className="p-6"><form onSubmit={handleSubmit}><div className="mb-6"><input ref={inputRef} type="password" className="w-full p-3 border-2 rounded-xl text-lg font-bold text-center" value={inputPin} onChange={e => {setInputPin(e.target.value); setError(false);}} placeholder="****"/>{error && <p className="text-xs text-red-500 mt-2 text-center">Incorrect PIN</p>}</div><button type="submit" className="w-full py-3 bg-blue-700 text-white rounded-xl font-bold">Unlock</button></form></div>
            </div>
        </div>
    );
};

const ItemOptionModal = ({ editingItem, setEditingItem, updateCartItemOptions }) => {
    if (!editingItem) return null;
    const finalPrice = calculateItemPrice(editingItem);
    return (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
            <div className="bg-white w-full max-w-sm rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
                <div className="relative h-32 bg-gray-100"><img src={editingItem.image} className="w-full h-full object-cover" alt=""/><button onClick={() => setEditingItem(null)} className="absolute top-3 right-3 bg-black/30 text-white p-1 rounded-full"><X size={18}/></button></div>
                <div className="p-5 overflow-y-auto">
                  <div className="mb-4"><label className="text-xs font-bold text-gray-500">Unit Price</label><input type="number" className="w-full p-3 border rounded-xl font-bold text-xl" value={editingItem.price} onChange={(e) => setEditingItem({...editingItem, price: Number(e.target.value)})}/></div>
                  <div className="mb-4 bg-gray-50 p-3 rounded-xl border"><div className="flex justify-between mb-2"><label className="text-xs font-bold">Discount</label><div className="flex bg-white rounded p-1 border"><button className={`px-2 py-0.5 text-xs rounded ${editingItem.discountType==='THB'?'bg-blue-700 text-white':''}`} onClick={() => setEditingItem({...editingItem, discountType: 'THB'})}>฿</button><button className={`px-2 py-0.5 text-xs rounded ${editingItem.discountType==='PERCENT'?'bg-blue-700 text-white':''}`} onClick={() => setEditingItem({...editingItem, discountType: 'PERCENT'})}>%</button></div></div><input type="number" className="w-full p-2 border rounded text-center text-red-500 font-bold" value={editingItem.discount} onChange={(e) => setEditingItem({...editingItem, discount: e.target.value})}/></div>
                  <div className="flex justify-between items-center mb-6 p-4 bg-blue-50 rounded-xl"><div className="text-sm font-medium text-blue-900">Net Price</div><div className="text-2xl font-bold text-blue-700">฿{finalPrice.toLocaleString()}</div></div>
                  <button onClick={() => { updateCartItemOptions(editingItem.id, editingItem.price, editingItem.discount, editingItem.discountType); setEditingItem(null); }} className="w-full py-3 bg-blue-700 text-white rounded-xl font-bold">Save</button>
                </div>
            </div>
        </div>
    );
};

const InventoryEditModal = ({ editingProduct, setEditingProduct, products, setProducts }) => {
    if (!editingProduct) return null;
    const handleSave = () => { setProducts(products.map(p => p.id === editingProduct.id ? editingProduct : p)); setEditingProduct(null); };
    return (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4">
            <div className="bg-white w-full max-w-sm rounded-2xl shadow-2xl p-5">
                <div className="flex justify-between mb-4"><h3 className="font-bold text-lg">Edit Product</h3><button onClick={() => setEditingProduct(null)}><X size={20}/></button></div>
                <div className="space-y-3">
                    <div><label className="text-xs font-bold">Name</label><input className="w-full p-2 border rounded" value={editingProduct.name} onChange={e => setEditingProduct({...editingProduct, name: e.target.value})} /></div>
                    <div><label className="text-xs font-bold">Price</label><input type="number" className="w-full p-2 border rounded" value={editingProduct.price} onChange={e => setEditingProduct({...editingProduct, price: Number(e.target.value)})} /></div>
                </div>
                <div className="mt-4 flex gap-2"><button onClick={() => setEditingProduct(null)} className="flex-1 py-2 border rounded">Cancel</button><button onClick={handleSave} className="flex-1 py-2 bg-blue-700 text-white rounded">Save</button></div>
            </div>
        </div>
    );
};

const ReceiptTemplate = ({ order, shopProfile, type, promptPayNumber, printWithQR }) => {
    if (!order) return null;
    return (
        <div id="printable-receipt">
            <div className={type === '80mm' ? 'slip-80mm' : 'page-a4'}>
                <div className="header">
                    <div className="shop-name">{shopProfile.name}</div>
                    {shopProfile.branch && <div>{shopProfile.branch}</div>}
                    <div>{shopProfile.address}</div>
                    <div>Tel: {shopProfile.phone}</div>
                    {shopProfile.taxId && <div>Tax ID: {shopProfile.taxId}</div>}
                </div>
                <div className="divider-dashed"></div>
                <div className="row"><span>Inv: {order.id}</span><span>{new Date(order.date).toLocaleString()}</span></div>
                <div className="row"><span>Staff: {order.staff}</span></div>
                <div className="row"><span>Cust: {order.customer?.name}</span></div>
                {order.isVoid && <div className="row"><span style={{color: 'red', fontWeight: 'bold'}}>*** VOID / CANCELLED ***</span></div>}
                <div className="divider-solid"></div>
                {order.items.map((item, idx) => (
                    <div key={idx} className="item-row">
                        <div className="item-name">{item.name} {item.discount > 0 && <span className="text-[10px]">(-{item.discount})</span>}</div>
                        <div className="item-details">
                            <span>{item.quantity} x {item.price.toLocaleString()}</span>
                            <span>{((item.price * item.quantity) - (item.discountType === 'PERCENT' ? (item.price*item.quantity*item.discount/100) : item.discount)).toLocaleString()}</span>
                        </div>
                    </div>
                ))}
                <div className="divider-solid"></div>
                <div className="totals">
                    <div className="row"><span>Total</span><span>฿{order.total.toLocaleString()}</span></div>
                </div>
                {(!order.isVoid && printWithQR && promptPayNumber) && (
                    <div className="qr-section">
                        <img src={getPromptPayUrl(order.total, promptPayNumber)} alt="QR" />
                        <div>Scan to Pay</div>
                    </div>
                )}
                <div className="footer">Thank you</div>
            </div>
        </div>
    );
};

// --- CART CONTENT ---
const CartContent = ({ cart, updateCartItem, subtotal, grandTotal, billDiscount, setBillDiscount, openCheckout, staffList, selectedStaff, setSelectedStaff, billDiscountType, setBillDiscountType, setEditingItem }) => (
  <>
    <div className="flex-1 overflow-y-auto p-4 space-y-3">
      {cart.length === 0 ? (<div className="flex flex-col items-center justify-center h-full text-gray-300 gap-2"><ShoppingCart size={40} /><p className="text-sm">Empty Cart</p></div>) :
        cart.map((item) => (
          <div key={item.id} className="flex gap-3 bg-white p-2 rounded-xl border border-gray-100 shadow-sm relative">
            <div className="w-14 h-14 bg-gray-100 rounded-lg overflow-hidden shrink-0">
              {item.image ? <img src={item.image} className="w-full h-full object-cover" alt={item.name} /> : <ImageIcon className="w-full h-full p-3 text-gray-300" />}
            </div>
            <div className="flex-1 min-w-0 flex flex-col justify-between">
              <div className="flex justify-between items-start">
                <div className="font-medium text-xs text-gray-800 line-clamp-1 mr-2">{item.name}</div>
                {item.discount > 0 && <span className="text-[10px] bg-red-100 text-red-600 px-1.5 py-0.5 rounded font-bold whitespace-nowrap">-{item.discount}{item.discountType === 'PERCENT' ? '%' : ''}</span>}
              </div>
              <div className="flex justify-between items-center mt-1">
                <div className="flex items-center gap-2">
                  <div className="text-blue-700 font-bold text-sm">
                    {item.discount > 0 ? (
                      <div className="flex flex-col">
                        <span className="line-through text-gray-300 text-[10px]">฿{item.price * item.quantity}</span>
                        <span>฿{(calculateItemPrice(item) * item.quantity).toLocaleString()}</span>
                      </div>
                    ) : `฿${(item.price * item.quantity).toLocaleString()}`}
                  </div>
                  <button onClick={() => setEditingItem(item)} className="p-1 rounded-full text-gray-400 hover:text-blue-700 hover:bg-gray-100 transition-colors"><Edit3 size={14} /></button>
                </div>
                <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-0.5">
                  <button onClick={() => updateCartItem(item.id, -1)} className="w-6 h-6 flex items-center justify-center bg-white rounded shadow-sm text-gray-600"><Minus size={12} /></button>
                  <span className="text-xs font-bold w-4 text-center">{item.quantity}</span>
                  <button onClick={() => updateCartItem(item.id, 1)} className="w-6 h-6 flex items-center justify-center bg-white rounded shadow-sm text-gray-600"><Plus size={12} /></button>
                </div>
              </div>
            </div>
          </div>
        ))
      }
    </div>
    <div className="p-4 bg-white border-t safe-area-bottom shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)] pb-8 md:pb-4">
      <div className="space-y-1 text-sm mb-3">
        <div className="flex justify-between text-gray-500 text-xs"><span>Subtotal</span><span>฿{subtotal.toLocaleString()}</span></div>
        <div className="flex justify-between items-center text-gray-500 text-xs"><span>Discount</span><div className="flex items-center gap-1"><input className="w-16 text-right p-1 border rounded text-xs" type="number" value={billDiscount} onChange={(e) => setBillDiscount(Number(e.target.value))} onFocus={(e) => e.target.select()} /><div className="flex bg-gray-100 rounded border p-0.5"><button onClick={() => setBillDiscountType('THB')} className={`px-1.5 py-0.5 text-[10px] rounded ${billDiscountType === 'THB' ? 'bg-white shadow text-black font-bold' : 'text-gray-400'}`}>฿</button><button onClick={() => setBillDiscountType('PERCENT')} className={`px-1.5 py-0.5 text-[10px] rounded ${billDiscountType === 'PERCENT' ? 'bg-white shadow text-black font-bold' : 'text-gray-400'}`}>%</button></div></div></div>
        <div className="flex justify-between text-lg font-bold text-gray-800 pt-2 border-t mt-2"><span>Total</span><span>฿{grandTotal.toLocaleString()}</span></div>
      </div>
      <div className="space-y-2">
        <div className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1 flex items-center gap-1"><User size={12} /> Sales Staff</div>
        <div className="relative">
          <select className="w-full p-2 pl-9 text-sm border rounded-lg bg-gray-50 appearance-none focus:ring-2 focus:ring-blue-500 outline-none" value={selectedStaff} onChange={(e) => setSelectedStaff(e.target.value)}>
            {staffList.map((s) => (<option key={s.id || s} value={s.name || s}>{s.name || s} {s.role ? `(${s.role})` : ''}</option>))}
          </select>
          <User className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
        </div>
        <button onClick={openCheckout} disabled={cart.length === 0} className="w-full py-3 bg-red-600 text-white rounded-xl font-bold shadow-lg shadow-red-200 active:scale-95 transition-all disabled:opacity-50 disabled:shadow-none text-sm mt-2">Charge ฿{grandTotal.toLocaleString()}</button>
      </div>
    </div>
  </>
);

// --- MAIN APP COMPONENT ---
export default function App() {
  const [view, setView] = useState('pos');
  const [isMobileCartOpen, setIsMobileCartOpen] = useState(false);
  const [pinModal, setPinModal] = useState({ isOpen: false, target: null, title: '' });
  const [financeUnlocked, setFinanceUnlocked] = useState(false);
  const [isScannerOpen, setIsScannerOpen] = useState(false);
  const [testBarcode, setTestBarcode] = useState('');
  const [scanErrorMsg, setScanErrorMsg] = useState('');
  const searchInputRef = useRef(null);

  // Data
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [orders, setOrders] = useState([]);

  // Settings & Printing
  const [staffList, setStaffList] = useState([{ id: 1, name: 'Admin', role: 'Manager', pin: '1234', image: '' }]);
  const [defaultCustomer, setDefaultCustomer] = useState({ name: 'Walk-in Customer', phone: '', address: '', zipcode: '', taxId: '' });
  const [promptPayAccounts, setPromptPayAccounts] = useState([{ id: 'default', name: 'Main Account', type: 'Phone', number: '0812345678' }]);
  const [activePromptPayId, setActivePromptPayId] = useState('default');
  const [newPromptPay, setNewPromptPay] = useState({ name: '', type: 'Phone', number: '' });
  const [editingPromptPayId, setEditingPromptPayId] = useState(null);
  const [shopProfile, setShopProfile] = useState({ name: 'Packhai Store', phone: '0812345678', address: 'Store Pickup', zipcode: '10000', taxId: '', warehouseId: 0, warehouseName: '' });
  const [token, setToken] = useState('');
  const [enableApi, setEnableApi] = useState(true);
  const [useProxy, setUseProxy] = useState(true);
  const [availableWarehouses, setAvailableWarehouses] = useState([]);
  const [newStaff, setNewStaff] = useState({ name: '', role: 'Staff', pin: '', image: '' });
  const [editingStaffId, setEditingStaffId] = useState(null);

  // Transaction State
  const [lastOrder, setLastOrder] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderDetailModal, setShowOrderDetailModal] = useState(false);
  const [checkoutStep, setCheckoutStep] = useState('idle');
  const [customerInfo, setCustomerInfo] = useState({ name: '', phone: '', address: '', zipcode: '', taxId: '' });
  const [selectedStaff, setSelectedStaff] = useState('Admin');
  const [billDiscount, setBillDiscount] = useState(0);
  const [billDiscountType, setBillDiscountType] = useState('THB');
  const [editingItem, setEditingItem] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);
  const [printWithQR, setPrintWithQR] = useState(true);
  const [loading, setLoading] = useState(false);
  const [syncStatus, setSyncStatus] = useState('idle');
  const [syncMsg, setSyncMsg] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [slipImage, setSlipImage] = useState(null);
  const [isUploadingSlip, setIsUploadingSlip] = useState(false);

  // ** New Printing State **
  const [printOrder, setPrintOrder] = useState(null);
  const [printType, setPrintType] = useState('80mm');
  const [printTrigger, setPrintTrigger] = useState(0);

  // --- INITIALIZE & PERSISTENCE ---
  // Function to fetch orders from Server (instead of LocalStorage)
  const fetchOrdersFromServer = async () => {
      try {
          const response = await fetch(`${DB_API_URL}?action=get_orders`);
          if (response.ok) {
              const data = await response.json();
              if (Array.isArray(data)) {
                  setOrders(data);
              }
          }
      } catch (error) {
          console.error("Failed to fetch orders:", error);
      }
  };

  useEffect(() => {
    const safeLoad = (key, setter, fallback) => {
      try {
        const saved = localStorage.getItem(key);
        if (saved) {
          const parsed = JSON.parse(saved);
          setter(parsed);
        } else if (fallback !== undefined) {
          setter(fallback);
        }
      } catch (e) { console.error(`Error loading ${key}`, e); }
    };
    safeLoad('my_shop_products', setProducts, []);
    // Load Orders from Server instead of local storage
    fetchOrdersFromServer();
    
    safeLoad('my_shop_staff', setStaffList, [{ id: 1, name: 'Admin', role: 'Manager', pin: '1234' }]);
    safeLoad('my_shop_default_customer', setDefaultCustomer, { name: 'Walk-in Customer', phone: '', address: '', zipcode: '', taxId: '' });
    safeLoad('my_shop_profile', setShopProfile, { name: 'Packhai Store', phone: '0812345678', address: 'Store Pickup', zipcode: '10000', taxId: '', warehouseId: 0 });
    safeLoad('my_shop_promptpay_accounts', setPromptPayAccounts, [{ id: 'default', name: 'Main Account', type: 'Phone', number: '0812345678' }]);
    const savedActivePP = localStorage.getItem('active_promptpay_id');
    if (savedActivePP) setActivePromptPayId(savedActivePP);
    const savedToken = localStorage.getItem('packhai_token');
    if (savedToken) setToken(savedToken);
    
    // Poll for new orders every 30 seconds (simple sync)
    const interval = setInterval(fetchOrdersFromServer, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => { if (products.length > 0) localStorage.setItem('my_shop_products', JSON.stringify(products)); }, [products]);
  // Don't save orders to localStorage anymore
  useEffect(() => { localStorage.setItem('my_shop_staff', JSON.stringify(staffList)); }, [staffList]);
  useEffect(() => { localStorage.setItem('my_shop_default_customer', JSON.stringify(defaultCustomer)); }, [defaultCustomer]);
  useEffect(() => { localStorage.setItem('my_shop_profile', JSON.stringify(shopProfile)); }, [shopProfile]);
  useEffect(() => { localStorage.setItem('packhai_token', token); }, [token]);
  useEffect(() => { localStorage.setItem('my_shop_promptpay_accounts', JSON.stringify(promptPayAccounts)); }, [promptPayAccounts]);
  useEffect(() => { localStorage.setItem('active_promptpay_id', activePromptPayId); }, [activePromptPayId]);

  // --- LOGIC ---
  const handleScan = useCallback((code) => {
    if (!code) return;
    if (view === 'settings') { setTestBarcode(code); return; }
    if (view === 'inventory') { setSearchTerm(code); return; }

    const matches = products.filter(p => (p.barcode && p.barcode === code) || (p.sku && p.sku === code) || (p.id.toString() === code));

    if (matches.length > 0) {
      let targetProduct;
      targetProduct = matches.find(p => {
        const cartItem = cart.find(c => c.id === p.id);
        if (cartItem) return cartItem.quantity < p.stock;
        return false;
      });

      if (!targetProduct) {
        targetProduct = matches.find(p => {
          const cartItem = cart.find(c => c.id === p.id);
          const currentQty = cartItem ? cartItem.quantity : 0;
          return p.stock > currentQty;
        });
      }

      if (!targetProduct) targetProduct = matches[0];
      const product = targetProduct;
      const cartItem = cart.find(c => c.id === product.id);
      const currentQty = cartItem ? cartItem.quantity : 0;

      if (product.stock <= currentQty) {
        beep(true);
        setScanErrorMsg(`Product ${product.name} Out of Stock!`);
        setTimeout(() => setScanErrorMsg(''), 2000);
        return;
      }

      setCart(prevCart => {
        const exist = prevCart.find(c => c.id === product.id);
        if (exist) {
          if (exist.quantity >= product.stock) return prevCart;
          return prevCart.map(c => c.id === product.id ? { ...c, quantity: c.quantity + 1 } : c);
        } else {
          return [...prevCart, { ...product, quantity: 1, discount: 0, discountType: 'THB' }];
        }
      });
      beep(false);
      setSearchTerm('');
      setScanErrorMsg('');
      if (searchInputRef.current) searchInputRef.current.focus();
    } else {
      beep(true);
      setScanErrorMsg(`Product not found: ${code}`);
      setSearchTerm('');
      setTimeout(() => setScanErrorMsg(''), 2000);
    }
  }, [products, view, cart]);

  useEffect(() => {
    let buffer = '';
    let lastKeyTime = Date.now();
    const handleKeyDown = (e) => {
      const currentTime = Date.now();
      const target = e.target;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') return;
      if (currentTime - lastKeyTime > 150) buffer = '';
      lastKeyTime = currentTime;
      if (e.key === 'Enter') {
        if (buffer.length > 0) { e.preventDefault(); handleScan(buffer); buffer = ''; }
      } else if (e.key.length === 1) { buffer += e.key; }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleScan]);

  const handleSetView = (targetView) => {
    if (targetView === 'settings') { setPinModal({ isOpen: true, target: 'settings', title: 'Settings Locked' }); } else { setView(targetView); }
  };

  const callApi = async (endpoint, method, body) => {
    let url = `${API_CONFIG.baseUrl}${endpoint}`;
    if (useProxy) url = `${PROXY_URL}${endpoint}`;
    try {
      const response = await fetch(url, {
        method, headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: body ? JSON.stringify(body) : undefined
      });
      if (!response.ok) throw new Error("API Error");
      return JSON.parse(await response.text());
    } catch (error) { throw error; }
  };

  const authorizeProxy = () => { window.open("https://cors-anywhere.herokuapp.com/corsdemo", "_blank"); };

  const handleApiError = (error, setMsg = alert) => {
    if (error.message === "PROXY_LOCKED" || error.message.includes("403")) {
      const msg = "⚠️ Proxy Locked: Please Authorize in Settings";
      setMsg(msg);
      if (setMsg === alert) alert(msg);
    } else {
      const msg = `❌ Error: ${error.message}`;
      setMsg(msg);
      if (setMsg === alert) alert(msg);
    }
  }

  const handleFetchWarehouses = async () => {
    if(!token) return alert("Please enter API Token first");
    setLoading(true);
    try {
        const res = await callApi(API_CONFIG.endpoints.getWarehouses, 'GET');
        if (res.warehouseList && res.warehouseList.length > 0) {
            setAvailableWarehouses(res.warehouseList);
            if (Number(shopProfile.warehouseId) === 0) {
                setShopProfile(prev => ({...prev, warehouseId: res.warehouseList[0].id, warehouseName: res.warehouseList[0].warehouseName}));
            }
            alert(`✅ Found ${res.warehouseList.length} warehouses`);
        } else { alert("⚠️ Token valid but no warehouses found"); }
    } catch (error) { handleApiError(error); } finally { setLoading(false); }
  };

  // *** THE SYNC LOGIC YOU WANTED ***
  const handleSyncStock = async () => {
    if (!token) return alert("Please enter API Token first");
    setLoading(true);
    setSyncStatus('idle');

    try {
      let allApiProducts = [];
      let skip = 0;
      const take = 200; 
      let keepFetching = true;

      while (keepFetching) {
          setSyncMsg(`Syncing... Fetched ${allApiProducts.length} items`);
          const payload = { skip: skip, take: take, warehouseID: Number(shopProfile.warehouseId) || 0 };

          const res = await callApi(API_CONFIG.endpoints.checkStock, 'POST', payload);
          const batch = res.stockInfo || res.data || [];
          
          if (!Array.isArray(batch) || batch.length === 0) {
              keepFetching = false;
          } else {
              allApiProducts = [...allApiProducts, ...batch];
              // If we got fewer items than requested, we've reached the end
              if (batch.length < take) {
                  keepFetching = false;
              } else {
                  skip += take;
              }
          }
          // Safety break
          if (skip > 10000) keepFetching = false; 
      }

      if (allApiProducts.length === 0) {
        setSyncStatus('error');
        setSyncMsg("No products found in this warehouse");
        return;
      }

      const updatedList = allApiProducts.map((item) => {
        const itemSku = item.barcode || item.productCode || item.sku || `ID-${item.id}`;
        const existing = products.find(p => p.id === item.stockID || p.sku === itemSku);

        return {
          id: item.stockID || item.id || Math.random(),
          skuId: item.skuId || item.skuID || 0,
          productId: item.productId || 0,
          stockId: item.stockID || 0,
          sku: item.sku || item.productCode || 'NO-SKU',
          barcode: item.barcode || item.Barcode || item.productBarcode || '',
          stock: Number(item.quantityAvailable ?? item.quantity ?? 0),
          image: item.photoLink || item.imageUrl || (existing ? existing.image : ''),
          name: existing ? existing.name : (item.name || item.productName || 'Packhai Product'),
          price: existing ? existing.price : Number((item.salePrice || item.unitPrice) || 0),
          onShelf: existing ? existing.onShelf : true
        };
      });

      setProducts(updatedList);
      setSyncStatus('success');
      setSyncMsg(`Successfully updated ${updatedList.length} items`);

    } catch (error) {
      setSyncStatus('error');
      handleApiError(error, (msg) => setSyncMsg(msg));
    } finally {
      setLoading(false);
    }
  };

  const { subtotal, totalItems, grandTotal } = useMemo(() => {
    let sub = 0; let count = 0;
    cart.forEach(item => { sub += calculateItemPrice(item) * item.quantity; count += item.quantity; });
    let disc = 0;
    if (billDiscount > 0) disc = billDiscountType === 'PERCENT' ? sub * billDiscount / 100 : billDiscount;
    return { subtotal: sub, totalItems: count, grandTotal: Math.max(0, sub - disc) };
  }, [cart, billDiscount, billDiscountType]);

  const prepareOrderPayload = () => {
      const billId = `POS-${new Date().toISOString().slice(2,10).replace(/-/g,'')}-${Math.floor(1000 + Math.random() * 9000)}`;
      const finalName = customerInfo.name.trim() || defaultCustomer.name || "Walk-in Customer";
      const finalPhone = customerInfo.phone.trim() || defaultCustomer.phone || "0000000000";
      const finalAddress = customerInfo.address.trim() || defaultCustomer.address || "Store Pickup";
      const finalZip = customerInfo.zipcode.trim() || defaultCustomer.zipcode || "10000";

      let totalDiscountAmount = 0;
      if (billDiscount > 0) {
          if (billDiscountType === 'PERCENT') {
              totalDiscountAmount = subtotal * billDiscount / 100;
          } else {
              totalDiscountAmount = billDiscount;
          }
      }

      return {
          referenceNumber: billId,
          orderStatusID: 30, // Default confirmed status
          warehouseID: Number(shopProfile.warehouseId) || 0,
          isCod: false,
          codAmount: 0,
          receiverName: finalName,
          receiverPhone: finalPhone,
          receiverAddress: finalAddress,
          receiverSubDistrict: "-",
          receiverDistrict: "-",
          receiverProvince: "-",
          receiverPostcode: finalZip,
          expressID: 0,
          trackingNo: "",
          totalProductPrice: Number(subtotal),
          deliveryPrice: 0,
          orderDiscount: Number(totalDiscountAmount),
          shippingDiscount: 0,
          vatType: 1,
          vatAmount: 0,
          remark: `POS Sale via PackhaiPOS | Staff: ${selectedStaff}`,
          productList: cart.map(item => {
              const realProduct = products.find(p => p.id === item.id) || item;
              const unitPrice = Number(item.price);
              const qty = Number(item.quantity);
              const finalPricePerUnit = calculateItemPrice(item);
              
              return {
                  productId: Number(realProduct.productId || 0),
                  skuId: Number(realProduct.skuId || 0),
                  stockId: Number(realProduct.stockId || 0),
                  sku: realProduct.sku || "",
                  productCode: realProduct.productCode || "",
                  barcode: realProduct.barcode || "",
                  productName: realProduct.name,
                  name: realProduct.name,
                  productImageUrl: realProduct.image || "",
                  quantity: qty,
                  unitPrice: unitPrice,
                  salePrice: finalPricePerUnit,
                  totalPrice: Number(finalPricePerUnit * qty)
              };
          }),
          _billId: billId
      };
  };

  const handleFileChange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      
      setIsUploadingSlip(true);
      
      // --- Upload to Server ---
      const formData = new FormData();
      formData.append('file', file);
      
      try {
          const response = await fetch(UPLOAD_URL, {
              method: 'POST',
              body: formData
          });
          const result = await response.json();
          if (result.status === 'success') {
              setSlipImage(result.url);
          } else {
              alert("Upload Failed: " + result.message);
          }
      } catch (error) {
          console.error("Upload error", error);
          alert("Upload Error: Check console.");
      } finally {
          setIsUploadingSlip(false);
      }
  };

  const handlePayment = async () => {
    setLoading(true);
    const orderPayload = prepareOrderPayload();
    const billId = orderPayload._billId;
    const payloadToSend = { ...orderPayload };
    delete payloadToSend._billId;

    let apiSuccess = false;
    let apiTracking = "";
    let apiErrorDetail = "";

    if (enableApi && token) {
        try {
            const resData = await callApi(API_CONFIG.endpoints.createOrder, 'POST', payloadToSend);
            if (resData.status === 'success' || resData.status === 'true' || resData.orderID) {
                apiTracking = resData.orderID || "API-OK";
                apiSuccess = true;
            } else {
                apiErrorDetail = resData.error_description || JSON.stringify(resData);
            }
        } catch (error) {
            console.error("Order Failed:", error);
            apiErrorDetail = error.message;
            if (error.message === "PROXY_LOCKED") apiErrorDetail = "Proxy Locked";
        }
    } else {
        apiErrorDetail = !token ? "No Token" : "API Disabled";
    }

    const newOrderRecord = {
      id: billId,
      date: new Date().toISOString(),
      items: [...cart],
      total: grandTotal,
      discount: billDiscount,
      customer: {
        name: orderPayload.receiverName,
        phone: orderPayload.receiverPhone,
        address: orderPayload.receiverAddress,
        zipcode: orderPayload.receiverPostcode,
        taxId: customerInfo.taxId || defaultCustomer.taxId
      },
      staff: selectedStaff,
      trackingNo: apiSuccess ? apiTracking : `OFF-${Math.floor(Date.now() / 1000)}`,
      apiStatus: apiSuccess ? 'Success' : 'Failed',
      apiError: apiErrorDetail,
      payload: payloadToSend,
      isVoid: false, // Initialize as not void
      slipImage: slipImage // Attach slip image
    };

    // Save to Server Database
    try {
        await fetch(`${DB_API_URL}?action=save_order`, {
            method: 'POST',
            body: JSON.stringify(newOrderRecord)
        });
    } catch (e) {
        console.error("Failed to save order to DB", e);
        alert("Warning: Saved to local only (Network Error)");
    }

    const updatedProducts = products.map(product => {
      const soldItem = cart.find(c => c.id === product.id);
      if (soldItem) return { ...product, stock: Math.max(0, product.stock - soldItem.quantity) };
      return product;
    });
    setProducts(updatedProducts);
    
    // Refresh orders list from server
    fetchOrdersFromServer();
    
    setLastOrder(newOrderRecord);
    setCart([]);
    setBillDiscount(0);
    setCustomerInfo({ name: '', phone: '', address: '', zipcode: '', taxId: '' });
    setSlipImage(null);
    setCheckoutStep('success');
    setLoading(false);
    
    // Voice Notification
    speakAmount(grandTotal);
  };

  const handleVoidOrder = async (orderId) => {
      if (!window.confirm(`Are you sure you want to VOID order ${orderId}? Stock will be restored.`)) return;

      // 1. Find the order
      const orderToVoid = orders.find(o => o.id === orderId);
      if (!orderToVoid) return;
      if (orderToVoid.isVoid) return alert("Order is already voided.");

      // 2. Restore Stock
      const updatedProducts = products.map(p => {
          const itemInOrder = orderToVoid.items.find(item => item.id === p.id);
          if (itemInOrder) {
              return { ...p, stock: p.stock + itemInOrder.quantity };
          }
          return p;
      });
      setProducts(updatedProducts);

      // 3. Update Order on Server
      try {
          await fetch(`${DB_API_URL}?action=update_order`, {
              method: 'POST',
              body: JSON.stringify({ id: orderId, isVoid: true, apiStatus: 'Cancelled' })
          });
          // Refresh orders
          fetchOrdersFromServer();
          alert("Order Cancelled & Stock Restored!");
      } catch (e) {
          alert("Failed to void order on server.");
      }
  };

  const handleUniversalPrint = async (type) => {
    const order = lastOrder || selectedOrder;
    if (!order) return alert("No order to print");
    setPrintOrder(order);
    setPrintType(type);
    setPrintTrigger(prev => prev + 1);
  };

  useEffect(() => {
    if (printTrigger > 0 && printOrder) {
        const timer = setTimeout(() => window.print(), 500);
        return () => clearTimeout(timer);
    }
  }, [printTrigger, printOrder]);

  const updateCartItem = (id, delta) => {
    const exist = cart.find(c => c.id === id);
    if (!exist) return;
    const product = products.find(p => p.id === id) || exist;
    const newQty = exist.quantity + delta;
    if (newQty <= 0) setCart(cart.filter(item => item.id !== id));
    else if (newQty > product.stock) { beep(true); alert("Max stock reached"); }
    else setCart(cart.map(item => item.id === id ? { ...item, quantity: newQty } : item));
  };

  const updateCartItemOptions = (id, newPrice, discount, type) => {
    setCart(cart.map(item => item.id === id ? { ...item, price: Number(newPrice), discount: Number(discount), discountType: type } : item));
  };

  const addToCart = (p) => {
    if (p.stock <= 0) { beep(true); return; }
    const exist = cart.find(c => c.id === p.id);
    if (exist && exist.quantity >= p.stock) { beep(true); return; }
    setCart(exist ? cart.map(c => c.id === p.id ? { ...c, quantity: c.quantity + 1 } : c) : [...cart, { ...p, quantity: 1, discount: 0, discountType: 'THB' }]);
    beep(false);
  };

  const handlePrintPreInvoice = async () => {
        const tempOrder = {
            id: `INV-${Math.floor(Date.now() / 1000)}`,
            date: new Date().toISOString(),
            items: cart,
            total: grandTotal,
            discount: billDiscount,
            customer: {
                name: customerInfo.name || defaultCustomer.name,
                phone: customerInfo.phone || defaultCustomer.phone,
                address: customerInfo.address || defaultCustomer.address,
                zipcode: customerInfo.zipcode || defaultCustomer.zipcode,
                taxId: customerInfo.taxId || defaultCustomer.taxId
            },
            staff: selectedStaff,
            trackingNo: "-",
            apiStatus: "Draft"
        };
        setPrintOrder(tempOrder);
        setPrintType('80mm');
        setPrintTrigger(prev => prev + 1);
  };

  const handleAddStaff = () => {
    if (!newStaff.name) return alert("Please enter staff name");
    setStaffList([...staffList, { ...newStaff, id: Date.now() }]);
    setNewStaff({ name: '', role: 'Staff', pin: '', image: '' });
  };

  const handleEditStaff = (staff) => { setNewStaff({ name: staff.name, role: staff.role, pin: staff.pin, image: staff.image }); setEditingStaffId(staff.id); };
  const handleUpdateStaff = () => { if (!newStaff.name) return alert("Please enter staff name"); setStaffList(staffList.map(s => s.id === editingStaffId ? { ...newStaff, id: editingStaffId } : s)); setNewStaff({ name: '', role: 'Staff', pin: '', image: '' }); setEditingStaffId(null); };
  const handleDeleteStaff = (id) => { if (staffList.length <= 1) return alert("Must have at least one staff member"); if (editingStaffId === id) { setNewStaff({ name: '', role: 'Staff', pin: '', image: '' }); setEditingStaffId(null); } setStaffList(staffList.filter(s => s.id !== id)); };

  const handleAddPromptPay = () => { if (!newPromptPay.number) return alert("Please enter ID/Phone number"); const newAcc = { ...newPromptPay, id: Date.now().toString() }; setPromptPayAccounts([...promptPayAccounts, newAcc]); setNewPromptPay({ name: '', type: 'Phone', number: '' }); };
  const handleEditPromptPay = (account) => { setNewPromptPay({ name: account.name, type: account.type, number: account.number }); setEditingPromptPayId(account.id); };
  const handleUpdatePromptPay = () => { if (!newPromptPay.number) return alert("Please enter ID/Phone number"); setPromptPayAccounts(promptPayAccounts.map(p => p.id === editingPromptPayId ? { ...newPromptPay, id: editingPromptPayId } : p)); setNewPromptPay({ name: '', type: 'Phone', number: '' }); setEditingPromptPayId(null); };
  const handleDeletePromptPay = (id) => { if(promptPayAccounts.length <= 1) return alert("Keep at least one account"); if(id === editingPromptPayId) { setNewPromptPay({ name: '', type: 'Phone', number: '' }); setEditingPromptPayId(null); } const newList = promptPayAccounts.filter(p => p.id !== id); setPromptPayAccounts(newList); if(activePromptPayId === id) setActivePromptPayId(newList[0].id); };

  const printStyles = `
    @media print {
        @page { margin: 0; size: auto; }
        body * { visibility: hidden; }
        #printable-receipt, #printable-receipt * { visibility: visible; }
        #printable-receipt { position: absolute; left: 0; top: 0; width: 100%; background: white; padding: 10px; color: black; z-index: 9999; }
        .slip-80mm { width: 80mm; font-size: 12px; font-family: 'Courier New', Courier, monospace; }
        .slip-80mm .header { text-align: center; margin-bottom: 10px; }
        .slip-80mm .shop-name { font-size: 16px; font-weight: bold; }
        .slip-80mm .divider-dashed { border-bottom: 1px dashed #000; margin: 5px 0; }
        .slip-80mm .divider-solid { border-bottom: 1px solid #000; margin: 5px 0; }
        .slip-80mm .row { display: flex; justify-content: space-between; margin-bottom: 2px; }
        .slip-80mm .item-row { margin-bottom: 4px; }
        .slip-80mm .item-name { font-weight: bold; }
        .slip-80mm .item-details { display: flex; justify-content: space-between; padding-left: 10px; }
        .slip-80mm .totals { margin-top: 10px; font-size: 14px; font-weight: bold; }
        .slip-80mm .qr-section { text-align: center; margin-top: 10px; }
        .slip-80mm .qr-section img { width: 100px; height: 100px; }
        .slip-80mm .footer { text-align: center; margin-top: 15px; font-size: 10px; }
        .page-a4 { width: 210mm; padding: 40px; font-family: sans-serif; font-size: 14px; }
        .page-a4 .header { text-align: center; margin-bottom: 20px; }
        .page-a4 .shop-name { font-size: 24px; font-weight: bold; }
        .page-a4 .divider-solid { border-bottom: 2px solid #ddd; margin: 20px 0; }
        .page-a4 .row { display: flex; justify-content: space-between; margin-bottom: 5px; }
        .page-a4 .item-row { display: flex; justify-content: space-between; border-bottom: 1px solid #eee; padding: 5px 0; }
        .page-a4 .totals { margin-top: 20px; font-size: 18px; text-align: right; }
        .page-a4 .totals .row { justify-content: flex-end; gap: 20px; }
        .page-a4 .qr-section { display: none; }
        .page-a4 .footer { text-align: center; margin-top: 40px; color: #666; }
    }
    @media screen { #printable-receipt { display: none; } }
  `;

  // --- RENDERERS ---
  const renderDashboard = () => {
      const today = new Date();
      const currentMonth = today.getMonth();
      const currentYear = today.getFullYear();
      const validOrders = orders.filter(o => !o.isVoid);

      const todaySales = validOrders
        .filter(o => new Date(o.date).toDateString() === today.toDateString())
        .reduce((sum, o) => sum + o.total, 0);
      const todayCount = validOrders.filter(o => new Date(o.date).toDateString() === today.toDateString()).length;

      const monthlyOrders = validOrders.filter(o => {
          const d = new Date(o.date);
          return d.getMonth() === currentMonth && d.getFullYear() === currentYear;
      });
      const monthlySales = monthlyOrders.reduce((sum, o) => sum + o.total, 0);

      const staffSales = {};
      monthlyOrders.forEach(o => { staffSales[o.staff] = (staffSales[o.staff] || 0) + o.total; });
      const topStaff = Object.entries(staffSales).sort(([, a], [, b]) => b - a).slice(0, 3);

      const productSales = {};
      monthlyOrders.forEach(o => {
          o.items.forEach(i => { productSales[i.name] = (productSales[i.name] || 0) + i.quantity; });
      });
      const topProducts = Object.entries(productSales).sort(([, a], [, b]) => b - a).slice(0, 5);

      return (
    <div className="flex-1 h-full overflow-y-auto p-4 bg-gray-50 pb-24 md:pb-4">
      <h2 className="text-xl md:text-2xl font-bold mb-4 md:mb-6 text-blue-900">Dashboard</h2>
      
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-white p-6 rounded-xl border border-blue-200 shadow-sm flex items-center justify-between">
            <div>
                <p className="text-sm text-gray-500 mb-1">Today's Sales</p>
                <h3 className="text-2xl font-bold text-gray-800">฿{todaySales.toLocaleString()}</h3>
                <p className="text-xs text-gray-400 mt-1">{todayCount} orders</p>
            </div>
            <div className="p-3 bg-blue-50 rounded-xl text-blue-600"><CreditCard size={24}/></div>
        </div>
        <div className="bg-white p-6 rounded-xl border border-blue-200 shadow-sm flex items-center justify-between">
            <div>
                <p className="text-sm text-gray-500 mb-1">Monthly Sales</p>
                <h3 className="text-2xl font-bold text-gray-800">฿{monthlySales.toLocaleString()}</h3>
                <p className="text-xs text-gray-400 mt-1">{monthlyOrders.length} orders</p>
            </div>
            <div className="p-3 bg-blue-50 rounded-xl text-blue-600"><Calendar size={24}/></div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
            <h3 className="font-bold text-gray-700 mb-4 flex items-center gap-2"><Trophy size={18} className="text-yellow-500"/> Top Staff (Monthly)</h3>
            {topStaff.length > 0 ? (
                <div className="space-y-3">
                    {topStaff.map(([name, total], index) => (
                        <div key={index} className="flex justify-between items-center p-2 bg-gray-50 rounded-lg">
                            <div className="flex items-center gap-3">
                                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${index === 0 ? 'bg-yellow-100 text-yellow-600' : 'bg-gray-200 text-gray-600'}`}>{index + 1}</div>
                                <span className="text-sm font-medium">{name}</span>
                            </div>
                            <span className="font-bold text-blue-700 text-sm">฿{total.toLocaleString()}</span>
                        </div>
                    ))}
                </div>
            ) : <p className="text-sm text-gray-400 text-center py-4">No sales data</p>}
        </div>
        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
            <h3 className="font-bold text-gray-700 mb-4 flex items-center gap-2"><TrendingUp size={18} className="text-red-500"/> Top Products (Monthly)</h3>
            {topProducts.length > 0 ? (
                <div className="space-y-3">
                    {topProducts.map(([name, qty], index) => (
                        <div key={index} className="flex justify-between items-center p-2 bg-gray-50 rounded-lg">
                            <div className="flex items-center gap-3 overflow-hidden">
                                <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-xs text-gray-600 shrink-0">{index + 1}</div>
                                <span className="text-sm font-medium truncate">{name}</span>
                            </div>
                            <span className="font-bold text-gray-600 text-xs bg-white px-2 py-1 rounded border">{qty} sold</span>
                        </div>
                    ))}
                </div>
            ) : <p className="text-sm text-gray-400 text-center py-4">No data</p>}
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-4 border-b font-semibold text-gray-700">Recent Orders</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left whitespace-nowrap">
            <thead className="bg-blue-50 text-blue-900">
              <tr>
                <th className="p-3">ID</th>
                <th className="p-3">Staff</th>
                <th className="p-3">Total</th>
                <th className="p-3 text-center">Status</th>
                <th className="p-3 text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              {orders.slice(0, 20).map(order => (
                <tr key={order.id} className={`border-b last:border-0 hover:bg-gray-50 transition-colors ${order.isVoid ? 'opacity-50 bg-gray-50' : ''}`}>
                  <td className="p-3">
                    <button onClick={() => { setSelectedOrder(order); setShowOrderDetailModal(true); }} className="font-medium text-blue-700 hover:underline">{order.id}</button>
                  </td>
                  <td className="p-3"><div className="flex items-center gap-2"><User size={14} className="text-gray-400"/> {order.staff}</div></td>
                  <td className={`p-3 font-bold ${order.isVoid ? 'line-through text-gray-400' : 'text-gray-800'}`}>฿{order.total.toLocaleString()}</td>
                  <td className="p-3 text-center">
                      <span className={`px-2 py-1 rounded-full text-[10px] font-bold ${order.isVoid ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-700'}`}>
                          {order.isVoid ? 'Cancelled' : 'Success'}
                      </span>
                  </td>
                  <td className="p-3 flex justify-center gap-2">
                    <button onClick={() => { setSelectedOrder(order); setShowOrderDetailModal(true); }} className="p-1.5 bg-gray-100 rounded text-gray-600 hover:bg-blue-100 hover:text-blue-700" title="Print"><Printer size={14} /></button>
                    {!order.isVoid && (
                        <button onClick={() => handleVoidOrder(order.id)} className="p-1.5 bg-red-50 rounded text-red-500 hover:bg-red-100 hover:text-red-700" title="Void Order"><XCircle size={14} /></button>
                    )}
                  </td>
                </tr>
              ))}
              {orders.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-gray-400">No orders</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
  };

  const renderInventory = () => (
    <div className="flex-1 flex flex-col h-full bg-gray-50 pb-20 md:pb-0">
      <div className="p-4 bg-white border-b flex flex-col gap-3 sticky top-0 z-10 shadow-sm">
        <div className="flex justify-between items-center">
            <h2 className="text-lg font-bold flex items-center gap-2"><Box className="text-blue-700" size={20} /> Stock</h2>
            <div className="flex gap-2">
            <button onClick={() => setIsScannerOpen(true)} className="p-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors"><Scan size={20} /></button>
            <button onClick={handleSyncStock} disabled={loading} className="px-3 py-1.5 bg-blue-700 text-white rounded-lg text-sm font-medium flex items-center gap-1">{loading ? <div className="animate-spin"><RefreshCw size={16}/></div> : <CloudLightning size={16} />} Sync</button>
            </div>
        </div>
        <div className="flex gap-2 items-center bg-gray-50 p-2 rounded-lg border border-gray-200">
            <Store size={16} className="text-gray-500 ml-1" />
            <select className="flex-1 bg-transparent text-sm font-medium outline-none text-gray-700" value={shopProfile.warehouseId} onChange={(e) => setShopProfile({...shopProfile, warehouseId: e.target.value})}>
                <option value="0">Select Warehouse...</option>
                {availableWarehouses.map(w => (<option key={w.id} value={w.id}>{w.warehouseName} (ID: {w.id})</option>))}
                {!availableWarehouses.find(w => w.id == shopProfile.warehouseId) && shopProfile.warehouseId != 0 && (<option value={shopProfile.warehouseId}>ID: {shopProfile.warehouseId}</option>)}
            </select>
            <button onClick={handleFetchWarehouses} className="text-xs bg-white border px-2 py-1.5 rounded shadow-sm hover:bg-gray-50 text-gray-600 font-medium">Fetch</button>
        </div>
        <div className="flex gap-4 text-sm text-gray-600 px-1 bg-gray-50 p-2 rounded border">
            <div>Total SKUs: <span className="font-bold text-blue-700 text-lg">{products.length}</span></div>
            <div>Total Stock: <span className="font-bold text-green-600 text-lg">{products.reduce((acc, p) => acc + p.stock, 0).toLocaleString()}</span></div>
        </div>
      </div>
      <div className="p-3 bg-white border-b">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <input className="w-full pl-9 pr-4 py-2 rounded-lg border border-gray-200 bg-gray-50 text-sm focus:bg-white focus:ring-2 focus:ring-blue-500 outline-none" placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
        </div>
      </div>
      {syncStatus === 'error' && (
        <div className="m-4 mb-0 p-4 bg-red-50 text-red-700 rounded-lg text-sm flex flex-col gap-2 border border-red-100">
          <div className="flex items-center gap-2 font-bold"><AlertCircle size={18} /> Error!</div>
          <div>{syncMsg}</div>
          <button onClick={authorizeProxy} className="mt-2 bg-red-100 text-red-800 px-3 py-2 rounded-lg font-bold self-start border border-red-200 shadow-sm flex items-center gap-2"><ExternalLink size={16} /> Authorize Proxy</button>
        </div>
      )}
      <div className="flex-1 overflow-auto p-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <table className="w-full text-sm text-left">
            <thead className="bg-gray-50 text-gray-600 font-bold border-b border-gray-200">
              <tr>
                <th className="p-3 pl-4">Name</th>
                <th className="p-3 hidden md:table-cell">SKU</th>
                <th className="p-3 hidden md:table-cell">Barcode</th>
                <th className="p-3 text-right">Price</th>
                <th className="p-3 text-center">Stock</th>
                <th className="p-3 text-center">Edit</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {products.filter(p => p.name.toLowerCase().includes(searchTerm.toLowerCase()) || p.sku.toLowerCase().includes(searchTerm.toLowerCase()) || (p.barcode && p.barcode.toLowerCase().includes(searchTerm.toLowerCase()))).map(p => (
                <tr key={p.id} className="hover:bg-blue-50/50 transition-colors">
                  <td className="p-3 pl-4">
                      <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-gray-100 overflow-hidden shrink-0 border border-gray-200">
                              {p.image ? <img src={p.image} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-gray-300"><ImageIcon size={16} /></div>}
                          </div>
                          <div>
                              <div className="font-medium text-gray-800 line-clamp-1">{p.name}</div>
                              <div className="md:hidden text-xs text-gray-500 mt-0.5">{p.sku}</div>
                          </div>
                      </div>
                  </td>
                  <td className="p-3 hidden md:table-cell text-gray-600 font-mono text-xs">{p.sku}</td>
                  <td className="p-3 hidden md:table-cell text-gray-600 font-mono text-xs">{p.barcode || '-'}</td>
                  <td className="p-3 text-right font-bold text-blue-700">฿{p.price.toLocaleString()}</td>
                  <td className="p-3 text-center"><span className={`px-2 py-1 rounded text-xs font-bold ${p.stock > 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{p.stock}</span></td>
                  <td className="p-3 text-center"><button onClick={() => setEditingProduct(p)} className="p-2 text-gray-400 hover:text-blue-700"><Edit3 size={16} /></button></td>
                </tr>
              ))}
              {products.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-gray-400">No products found.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderSettings = () => (
    <div className="flex-1 h-full overflow-y-auto p-4 bg-gray-50 pb-24 md:pb-4">
      <h2 className="text-xl font-bold mb-4 text-gray-800 flex items-center gap-2">Settings <span className="text-xs bg-gray-200 px-2 py-1 rounded text-gray-500 font-normal">Unlocked</span></h2>
      <div className="max-w-2xl mx-auto space-y-4">
        
        {/* Hardware Settings */}
        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
            <h3 className="text-md font-semibold mb-3 flex items-center gap-2 text-gray-700"><Scan size={18}/> Hardware & Devices</h3>
            <div className="space-y-2">
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg"><div className="text-sm font-medium">Scanner Connection</div><span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded font-bold">Active (Plug & Play)</span></div>
                <div className="p-3 border rounded-lg bg-gray-50"><div className="text-xs text-gray-500 mb-1 font-bold">TEST SCANNER</div><input className="w-full p-2 text-sm border rounded bg-white" placeholder="Scan barcode here to test..." value={testBarcode} onChange={e => setTestBarcode(e.target.value)} />{testBarcode && <div className="mt-1 text-xs text-green-600 font-bold">Read: {testBarcode}</div>}</div>
            </div>
        </div>

        {/* Finance Settings */}
        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 relative overflow-hidden">
          <h3 className="text-md font-semibold mb-3 flex items-center gap-2 text-red-600"><Wallet size={18} /> Finance (PromptPay)</h3>
          {!financeUnlocked ? (
            <div className="absolute inset-0 bg-white/90 backdrop-blur-sm z-10 flex flex-col items-center justify-center"><Lock className="text-gray-400 mb-2" size={32} /><button onClick={() => setPinModal({ isOpen: true, target: 'finance', title: 'Unlock Finance' })} className="bg-red-600 text-white px-4 py-2 rounded-lg font-bold">Enter PIN</button></div>
          ) : (
            <div className="space-y-4">
              <div className="bg-red-50 p-3 rounded-lg border border-red-100"><h4 className="text-xs font-bold text-red-800 uppercase mb-2">Active PromptPay</h4><select className="w-full p-2 text-sm border border-red-200 rounded-lg bg-white" value={activePromptPayId} onChange={(e) => setActivePromptPayId(e.target.value)}>{promptPayAccounts.map(pp => (<option key={pp.id} value={pp.id}>{pp.name} ({pp.number})</option>))}</select></div>
              <div className="bg-gray-50 p-3 rounded-lg border border-gray-200"><h4 className="text-xs font-bold text-gray-500 uppercase mb-2">{editingPromptPayId ? 'Edit Account' : 'Add New Account'}</h4><div className="grid grid-cols-2 gap-2 mb-2"><input className="p-2 border rounded-lg text-sm col-span-2" placeholder="Account Name (e.g. SCB)" value={newPromptPay.name} onChange={e => setNewPromptPay({...newPromptPay, name: e.target.value})} /><select className="p-2 border rounded-lg text-sm bg-white" value={newPromptPay.type} onChange={e => setNewPromptPay({...newPromptPay, type: e.target.value})}><option value="Phone">Phone</option><option value="ID">Citizen ID</option></select><input className="p-2 border rounded-lg text-sm" placeholder="Number" value={newPromptPay.number} onChange={e => setNewPromptPay({...newPromptPay, number: e.target.value})} /></div>{editingPromptPayId ? (<div className="flex gap-2"><button onClick={handleUpdatePromptPay} className="flex-1 py-2 bg-red-600 text-white rounded-lg text-sm font-bold flex justify-center items-center gap-2"><Save size={16}/> Update</button><button onClick={() => { setNewPromptPay({ name: '', type: 'Phone', number: '' }); setEditingPromptPayId(null); }} className="flex-1 py-2 bg-gray-400 text-white rounded-lg text-sm font-bold flex justify-center items-center gap-2"><X size={16}/> Cancel</button></div>) : (<button onClick={handleAddPromptPay} className="w-full py-2 bg-red-600 text-white rounded-lg text-sm font-bold flex justify-center items-center gap-2"><PlusCircle size={16}/> Add Account</button>)}</div>
              <div className="space-y-2">{promptPayAccounts.map((pp) => (<div key={pp.id} className={`flex justify-between items-center p-3 border rounded-lg hover:bg-gray-50 transition-colors ${activePromptPayId === pp.id ? 'border-red-500 bg-red-50' : ''}`}><div><div className="font-bold text-sm text-gray-800">{pp.name} {activePromptPayId === pp.id && <span className="text-xs text-red-600 bg-red-100 px-1 rounded ml-1">Active</span>}</div><div className="text-xs text-gray-500">{pp.type}: {pp.number}</div></div><div className="flex gap-1"><button onClick={() => handleEditPromptPay(pp)} className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg transition-colors"><Edit size={16}/></button>{promptPayAccounts.length > 1 && (<button onClick={() => handleDeletePromptPay(pp.id)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"><Trash size={16}/></button>)}</div></div>))}</div>
            </div>
          )}
        </div>

        {/* API Settings */}
        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
          <h3 className="text-md font-semibold mb-3 flex items-center gap-2 text-blue-700"><CloudLightning size={18} /> API & Stock</h3>
          <div className="space-y-3">
            <input type="password" value={token} onChange={(e) => setToken(e.target.value)} placeholder="Packhai Token..." className="w-full p-2.5 rounded-lg border border-gray-200 text-sm" />
            <div className="flex gap-2">
                <input type="number" value={shopProfile.warehouseId ?? ''} onChange={(e) => setShopProfile({...shopProfile, warehouseId: e.target.value})} placeholder="Warehouse ID" className="flex-1 p-2.5 rounded-lg border border-gray-200 text-sm"/>
                <button onClick={handleFetchWarehouses} disabled={loading} className="bg-blue-700 text-white px-3 rounded-lg text-sm hover:bg-blue-800 disabled:opacity-50">{loading ? '...' : 'Fetch'}</button>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"><div className="text-sm">Use Proxy</div><button onClick={() => setUseProxy(!useProxy)} className={`w-10 h-5 rounded-full relative transition-colors ${useProxy ? 'bg-blue-700' : 'bg-gray-300'}`}><div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${useProxy ? 'translate-x-5' : ''}`} /></button></div>
            {useProxy && <button onClick={authorizeProxy} className="text-xs text-blue-600 underline block w-full text-left font-bold">Authorize Proxy</button>}
          </div>
        </div>

        {/* Shop Info - RESTORED */}
        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
            <h3 className="text-md font-semibold mb-3 text-orange-500 flex items-center gap-2"><Store size={18}/> Shop Info</h3>
            <div className="space-y-3">
            <input className="w-full p-2.5 border rounded-lg text-sm" placeholder="Shop Name" value={shopProfile.name} onChange={e => setShopProfile({...shopProfile, name: e.target.value})}/>
            <input className="w-full p-2.5 border rounded-lg text-sm" placeholder="Branch Name (Optional)" value={shopProfile.branch} onChange={e => setShopProfile({...shopProfile, branch: e.target.value})}/>
            <textarea className="w-full p-2.5 border rounded-lg text-sm" placeholder="Shop Address (Optional)" rows="2" value={shopProfile.address} onChange={e => setShopProfile({...shopProfile, address: e.target.value})}/>
            <input className="w-full p-2.5 border rounded-lg text-sm" placeholder="Phone" value={shopProfile.phone} onChange={e => setShopProfile({...shopProfile, phone: e.target.value})}/>
            <input className="w-full p-2.5 border rounded-lg text-sm" placeholder="Tax ID / เลขประจำตัวผู้เสียภาษี (Optional)" value={shopProfile.taxId} onChange={e => setShopProfile({...shopProfile, taxId: e.target.value})}/>
            </div>
        </div>

        {/* Staff Management - RESTORED/CHECKED */}
        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
            <h3 className="text-md font-semibold mb-3 flex items-center gap-2 text-blue-700"><Users size={18}/> Staff Management</h3>
            <div className="bg-gray-50 p-3 rounded-lg border border-gray-200 mb-3">
                <h4 className="text-xs font-bold text-gray-500 uppercase mb-2">{editingStaffId ? 'Edit Staff' : 'Add New Staff'}</h4>
                <div className="grid grid-cols-2 gap-2 mb-2">
                  <input className="p-2 border rounded-lg text-sm" placeholder="Name" value={newStaff.name} onChange={e => setNewStaff({...newStaff, name: e.target.value})} />
                  <input className="p-2 border rounded-lg text-sm" placeholder="Role (e.g. Manager)" value={newStaff.role} onChange={e => setNewStaff({...newStaff, role: e.target.value})} />
                  <input className="p-2 border rounded-lg text-sm col-span-2" placeholder="PIN/Password" value={newStaff.pin} onChange={e => setNewStaff({...newStaff, pin: e.target.value})} />
                </div>
                {editingStaffId ? (
                    <div className="flex gap-2">
                        <button onClick={handleUpdateStaff} className="flex-1 py-2 bg-blue-700 text-white rounded-lg text-sm font-bold flex justify-center items-center gap-2"><Save size={16}/> Update</button>
                        <button onClick={() => { setNewStaff({ name: '', role: 'Staff', pin: '', image: '' }); setEditingStaffId(null); }} className="flex-1 py-2 bg-gray-400 text-white rounded-lg text-sm font-bold flex justify-center items-center gap-2"><X size={16}/> Cancel</button>
                    </div>
                ) : (
                    <button onClick={handleAddStaff} className="w-full py-2 bg-blue-700 text-white rounded-lg text-sm font-bold flex justify-center items-center gap-2"><UserPlus size={16}/> Add Staff</button>
                )}
            </div>
            <div className="space-y-2">
                {staffList.map((staff, idx) => (
                    <div key={staff.id || idx} className="flex justify-between items-center p-3 border rounded-lg hover:bg-gray-50 transition-colors">
                        <div className="flex items-center gap-3"><div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-500"><User size={16}/></div><div><div className="font-bold text-sm text-gray-800">{staff.name}</div><div className="text-xs text-gray-500">{staff.role}</div></div></div>
                        <div className="flex gap-1"><button onClick={() => handleEditStaff(staff)} className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg transition-colors"><Edit size={16}/></button>{staffList.length > 1 && (<button onClick={() => handleDeleteStaff(staff.id)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"><Trash size={16}/></button>)}</div>
                    </div>
                ))}
            </div>
        </div>

        {/* Default Customer Info - RESTORED */}
        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100"><h3 className="text-md font-semibold mb-3 text-green-600 flex items-center gap-2"><User size={18}/> Default Customer Info</h3><div className="space-y-3"><div className="text-xs text-gray-400">ข้อมูลนี้จะถูกใช้เมื่อไม่ได้ระบุข้อมูลลูกค้าในหน้าชำระเงิน</div><input className="w-full p-2.5 border rounded-lg text-sm" placeholder="Default Name (e.g. Walk-in)" value={defaultCustomer.name} onChange={e => setDefaultCustomer({...defaultCustomer, name: e.target.value})}/><input className="w-full p-2.5 border rounded-lg text-sm" placeholder="Default Phone" value={defaultCustomer.phone} onChange={e => setDefaultCustomer({...defaultCustomer, phone: e.target.value})}/><textarea className="w-full p-2.5 border rounded-lg text-sm" placeholder="Default Address" rows="2" value={defaultCustomer.address} onChange={e => setDefaultCustomer({...defaultCustomer, address: e.target.value})}/><input className="w-full p-2.5 border rounded-lg text-sm" placeholder="Default Zipcode" value={defaultCustomer.zipcode} onChange={e => setDefaultCustomer({...defaultCustomer, zipcode: e.target.value})}/><input className="w-full p-2.5 border rounded-lg text-sm" placeholder="Default Tax ID" value={defaultCustomer.taxId} onChange={e => setDefaultCustomer({...defaultCustomer, taxId: e.target.value})}/></div></div>

      </div>
    </div>
  );

  // Modal for Order Details (Added Back)
  const OrderDetailModal = () => {
    if (!showOrderDetailModal || !selectedOrder) return null;
    return (
      <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm animate-fade-in">
        <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[85vh]">
          <div className="p-4 border-b flex justify-between items-center bg-gray-50">
            <h3 className="font-bold text-lg text-gray-800">Order: {selectedOrder.id}</h3>
            <button onClick={() => setShowOrderDetailModal(false)} className="p-2 hover:bg-gray-200 rounded-full"><X size={20}/></button>
          </div>
          <div className="p-4 overflow-y-auto flex-1">
            <div className="grid grid-cols-2 gap-4 mb-4 text-sm text-gray-600 bg-gray-50 p-3 rounded-xl">
              <div><span className="block text-xs text-gray-400">Date</span>{new Date(selectedOrder.date).toLocaleString()}</div>
              <div><span className="block text-xs text-gray-400">Status</span>{selectedOrder.isVoid ? <span className="text-red-600 font-bold">Cancelled</span> : selectedOrder.apiStatus}</div>
              <div><span className="block text-xs text-gray-400">Staff</span>{selectedOrder.staff}</div>
              <div><span className="block text-xs text-gray-400">Customer</span>{selectedOrder.customer.name}</div>
              <div className="col-span-2"><span className="block text-xs text-gray-400">Address</span>{selectedOrder.customer.address || '-'}</div>
            </div>
            
            {/* Slip Image Display */}
            {selectedOrder.slipImage && (
                <div className="mb-4 border rounded-lg overflow-hidden">
                    <div className="bg-gray-100 p-2 text-xs font-bold text-gray-500 flex items-center gap-2"><FileImage size={14}/> Payment Slip</div>
                    <img src={selectedOrder.slipImage} alt="Slip" className="w-full h-auto" />
                </div>
            )}

            <table className="w-full text-sm mb-4">
              <thead className="bg-gray-100 text-gray-600"><tr><th className="p-2 text-left rounded-l-lg">Item</th><th className="p-2 text-right">Qty</th><th className="p-2 text-right rounded-r-lg">Total</th></tr></thead>
              <tbody>
                {selectedOrder.items.map((item, idx) => (
                  <tr key={idx} className="border-b last:border-0 border-gray-100">
                    <td className="p-2"><div className="font-medium text-gray-800">{item.name}</div><div className="text-xs text-gray-400">{item.sku}</div></td>
                    <td className="p-2 text-right">{item.quantity}</td>
                    <td className="p-2 text-right">฿{(item.price * item.quantity).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex justify-between items-center pt-4 border-t border-dashed">
              <span className="font-bold text-lg">Total</span>
              <span className={`font-bold text-xl ${selectedOrder.isVoid ? 'text-gray-400 line-through' : 'text-blue-700'}`}>฿{selectedOrder.total.toLocaleString()}</span>
            </div>
          </div>
          <div className="p-4 border-t bg-gray-50 flex gap-2">
            <button onClick={() => { handleUniversalPrint('80mm'); setShowOrderDetailModal(false); }} className="flex-1 py-3 bg-blue-700 text-white rounded-xl font-bold flex items-center justify-center gap-2"><Printer size={18}/> Print</button>
            {!selectedOrder.isVoid && (
                <button onClick={() => { handleVoidOrder(selectedOrder.id); setShowOrderDetailModal(false); }} className="px-4 py-3 bg-red-100 text-red-600 rounded-xl font-bold hover:bg-red-200"><XCircle size={20}/></button>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="h-[100dvh] w-full flex flex-col md:flex-row font-sans bg-gray-100 text-gray-900 overflow-hidden">
      <style>{printStyles}</style>
      <div className="flex-1 flex flex-col overflow-hidden relative order-1 md:order-2 h-full">
        {view === 'pos' && (
          <div className="flex-1 flex flex-col md:flex-row h-full overflow-hidden relative bg-gray-50">
            <div className="flex-1 flex flex-col h-full relative">
              <div className="p-3 bg-white border-b sticky top-0 z-10">
                {scanErrorMsg && (<div className="mb-2 bg-red-100 border border-red-400 text-red-700 px-4 py-2 rounded relative animate-bounce-short text-center font-bold shadow-md"><AlertCircle className="inline mr-2" size={18} /> {scanErrorMsg}</div>)}
                <div className="flex justify-between items-center gap-3">
                  <div className="relative flex-1"><Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} /><input ref={searchInputRef} className="w-full pl-9 pr-4 py-2.5 rounded-lg border-2 border-blue-100 bg-blue-50/30 text-lg font-medium focus:bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-100 outline-none transition-all placeholder-gray-400 text-gray-800" placeholder="Scan Barcode..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} autoFocus onKeyDown={(e) => { if (e.key === 'Enter' && searchTerm) handleScan(searchTerm); }} /></div>
                  <button onClick={() => setIsScannerOpen(true)} className="p-2.5 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors"><Scan size={24} /></button>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-3 pb-24 md:pb-3">
                {products.length === 0 ? (<div className="h-64 flex flex-col items-center justify-center text-gray-400 text-sm"><Box size={40} className="mb-2 opacity-20" /> No products.<button onClick={() => setView('inventory')} className="text-blue-700 mt-1">Sync Stock</button></div>) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2 md:gap-3">
                    {products.filter(p => p.name.toLowerCase().includes(searchTerm.toLowerCase()) || p.sku.toLowerCase().includes(searchTerm.toLowerCase()) || (p.barcode && p.barcode.toLowerCase().includes(searchTerm.toLowerCase()))).map(p => {
                      const inCart = cart.find(c => c.id === p.id);
                      const qty = inCart ? inCart.quantity : 0;
                      return (
                        <div key={p.id} onClick={() => p.stock > 0 && addToCart(p)} className={`bg-white rounded-xl border shadow-sm flex flex-col gap-2 relative active:scale-95 transition-all overflow-hidden cursor-pointer ${qty > 0 ? 'border-blue-500 ring-2 ring-blue-50' : 'border-gray-100'} ${p.stock <= 0 ? 'opacity-60 grayscale' : ''}`}>
                          <div className="aspect-square bg-gray-100 relative">
                            {p.image ? <img src={p.image} className="w-full h-full object-cover" alt={p.name} /> : <div className="w-full h-full flex items-center justify-center text-gray-300"><ImageIcon size={24} /></div>}
                            {p.stock <= 0 && <div className="absolute inset-0 bg-black/50 flex items-center justify-center text-white text-xs font-bold">SOLD OUT</div>}
                            {qty > 0 && (<div className="absolute top-2 right-2 bg-red-500 text-white w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shadow-md animate-bounce-short">{qty}</div>)}
                          </div>
                          <div className="p-2 pt-0">
                            <div className="font-medium text-xs text-gray-800 line-clamp-2 h-8 leading-snug">{p.name}</div>
                            <div className="flex justify-between items-center mt-1"><span className="text-[10px] text-gray-500">Stock: {p.stock}</span></div>
                            <div className="flex justify-between items-end mt-1"><span className="font-bold text-blue-700 text-sm">฿{p.price.toLocaleString()}</span><div className={`w-6 h-6 rounded-full flex items-center justify-center transition-colors ${qty > 0 ? 'bg-blue-700 text-white' : 'bg-gray-100 text-gray-400'}`}><Plus size={14} strokeWidth={3} /></div></div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
            <div className="hidden md:flex w-80 bg-white border-l flex-col shadow-xl z-20"><div className="p-4 font-bold border-b text-lg">Current Order</div><CartContent cart={cart} updateCartItem={updateCartItem} subtotal={subtotal} grandTotal={grandTotal} billDiscount={billDiscount} setBillDiscount={setBillDiscount} openCheckout={() => setCheckoutStep('confirm')} staffList={staffList} selectedStaff={selectedStaff} setSelectedStaff={setSelectedStaff} billDiscountType={billDiscountType} setBillDiscountType={setBillDiscountType} setEditingItem={setEditingItem} /></div>
            {isMobileCartOpen && (<div className="md:hidden fixed inset-0 z-[60] flex items-end justify-center"><div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setIsMobileCartOpen(false)} /><div className="bg-white w-full h-[90vh] rounded-t-2xl shadow-2xl flex flex-col relative z-10 animate-slide-up"><div className="flex items-center justify-between p-4 border-b bg-gray-50 rounded-t-2xl"><h3 className="font-bold text-lg">Cart ({totalItems})</h3><button onClick={() => setIsMobileCartOpen(false)} className="p-2 bg-white rounded-full text-gray-500"><X size={20} /></button></div><CartContent cart={cart} updateCartItem={updateCartItem} subtotal={subtotal} grandTotal={grandTotal} billDiscount={billDiscount} setBillDiscount={setBillDiscount} openCheckout={() => { setCheckoutStep('confirm'); setIsMobileCartOpen(false); }} staffList={staffList} selectedStaff={selectedStaff} setSelectedStaff={setSelectedStaff} billDiscountType={billDiscountType} setBillDiscountType={setBillDiscountType} setEditingItem={setEditingItem} /></div></div>)}
            <div className="md:hidden fixed bottom-[80px] right-4 z-30"><button onClick={() => setIsMobileCartOpen(true)} className="bg-blue-700 text-white p-4 rounded-full shadow-lg shadow-blue-300 flex items-center justify-center relative active:scale-90 transition-transform"><ShoppingCart size={24} />{totalItems > 0 && <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center border-2 border-white">{totalItems}</span>}</button></div>
          </div>
        )}
        {view === 'inventory' && renderInventory()}
        {view === 'settings' && renderSettings()}
        {view === 'dashboard' && renderDashboard()}
      </div>
      <div className="bg-white border-t md:border-t-0 md:border-r border-gray-200 flex md:flex-col justify-between items-center p-2 md:w-20 md:h-full shrink-0 z-50 order-2 md:order-1 safe-area-bottom shadow-[0_-4px_10px_rgba(0,0,0,0.05)] md:shadow-none">
        <div className="flex md:flex-col gap-1 w-full justify-around md:justify-start md:mt-4">
          <div className="hidden md:flex bg-blue-700 p-2 rounded-xl text-white shadow-lg shadow-blue-200 mb-6 justify-center"><Package size={24} /></div>
          <NavButton id="dashboard" icon={<LayoutDashboard size={20} />} label="Dash" active={view === 'dashboard'} onClick={setView} />
          <NavButton id="pos" icon={<ShoppingCart size={20} />} label="POS" active={view === 'pos'} onClick={setView} />
          <NavButton id="inventory" icon={<Box size={20} />} label="Stock" active={view === 'inventory'} onClick={setView} />
          <NavButton id="settings" icon={<Settings size={20} />} label="Set" active={view === 'settings'} onClick={(id) => handleSetView(id)} />
        </div>
      </div>
      {checkoutStep === 'confirm' && (
        <div className="fixed inset-0 z-[60] flex items-end md:items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl w-full max-w-sm overflow-hidden flex flex-col max-h-[90vh] animate-scale-up">
            <div className="p-4 border-b bg-gray-50 flex justify-between items-center">
              <h3 className="font-bold text-gray-800 text-lg">Payment</h3>
              <div className="flex gap-2">
                  <button onClick={handlePrintPreInvoice} title="Print Bill" className="p-2 bg-gray-100 rounded-lg text-blue-700 hover:bg-blue-50"><Printer size={20}/></button>
                  <button onClick={() => setCheckoutStep('idle')}><X size={24} className="text-gray-500" /></button>
              </div>
            </div>
            <div className="p-6 overflow-y-auto">
              <div className="text-center mb-6">
                <div className="text-xs text-gray-500 mb-1">Total Amount</div>
                <div className="text-4xl font-bold text-blue-700">฿{grandTotal.toLocaleString()}</div>
              </div>
              <div className="flex flex-col items-center justify-center p-4 bg-white border-2 border-blue-100 rounded-2xl mb-6 shadow-sm relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-1 bg-blue-500"></div>
                <img src={getPromptPayUrl(grandTotal, promptPayAccounts.find(p => p.id === activePromptPayId)?.number)} className="w-48 h-48 mix-blend-multiply" alt="PromptPay QR" />
                <div className="flex items-center gap-2 mt-3 bg-blue-50 px-3 py-1 rounded-full border border-blue-100"><QrCode size={16} className="text-blue-700" /><span className="font-bold text-blue-900 text-sm tracking-wide">PromptPay</span></div>
                <div className="text-xs text-gray-400 mt-2 font-mono">{promptPayAccounts.find(p => p.id === activePromptPayId)?.number || '0000000000'}</div>
              </div>

              {/* Slip Attachment Section */}
              <div className="mb-4">
                  <label className="block text-xs font-semibold text-gray-500 mb-2">หลักฐานการโอน (Slip)</label>
                  <div className="flex items-center gap-2">
                      <label className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg cursor-pointer hover:bg-blue-100 transition-colors border border-blue-200">
                          <Camera size={18} />
                          <span className="text-sm font-bold">ถ่ายรูป / อัพโหลดสลิป</span>
                          <input type="file" accept="image/*" capture="environment" className="hidden" onChange={handleFileChange} />
                      </label>
                      {isUploadingSlip && <div className="text-xs text-blue-500 flex items-center gap-1"><Loader className="animate-spin" size={12}/> กำลังอัปโหลด...</div>}
                      {slipImage && !isUploadingSlip && <span className="text-xs text-green-600 font-bold flex items-center gap-1"><CheckCircle size={12}/> แนบแล้ว</span>}
                  </div>
                  {slipImage && (
                      <div className="mt-2 relative w-full h-32 rounded-lg overflow-hidden border border-gray-200">
                          <img src={slipImage} className="w-full h-full object-cover" alt="Slip" />
                          <button onClick={() => setSlipImage(null)} className="absolute top-1 right-1 bg-black/50 text-white p-1 rounded-full hover:bg-red-500 transition-colors"><X size={14}/></button>
                      </div>
                  )}
              </div>

              <div className="space-y-4">
                <h4 className="font-bold text-gray-700 flex items-center gap-2"><User size={16} /> Customer Info</h4>
                <input className="w-full p-3 border rounded-xl text-sm" placeholder={defaultCustomer.name || "Customer Name"} value={customerInfo.name} onChange={e => setCustomerInfo({ ...customerInfo, name: e.target.value })} />
                <input className="w-full p-3 border rounded-xl text-sm" placeholder={defaultCustomer.phone || "Phone"} value={customerInfo.phone} onChange={e => setCustomerInfo({ ...customerInfo, phone: e.target.value })} />
                {/* Added Address and Tax ID fields */}
                <textarea className="w-full p-3 border rounded-xl text-sm" placeholder="ที่อยู่ (Address)" rows="2" value={customerInfo.address} onChange={e => setCustomerInfo({...customerInfo, address: e.target.value})} />
                <input className="w-full p-3 border rounded-xl text-sm" placeholder="เลขประจำตัวผู้เสียภาษี (Tax ID)" value={customerInfo.taxId} onChange={e => setCustomerInfo({...customerInfo, taxId: e.target.value})} />
              </div>
            </div>
            <div className="p-4 border-t pb-8 md:pb-4 bg-gray-50">
              <button onClick={handlePayment} disabled={loading} className="w-full py-3.5 bg-red-600 text-white rounded-xl font-bold flex justify-center items-center gap-2 text-base shadow-lg shadow-red-200 active:scale-95 transition-all disabled:opacity-70">{loading ? 'Processing...' : 'Confirm Payment'}</button>
            </div>
          </div>
        </div>
      )}
      {checkoutStep === 'success' && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl w-full max-w-xs p-6 text-center animate-scale-up shadow-2xl">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 text-green-600 ring-4 ring-green-50"><CheckCircle size={32} /></div>
            <h3 className="text-xl font-bold text-gray-800 mb-1">Payment Success!</h3>
            <p className="text-sm text-gray-500 mb-6">Order ID: {lastOrder?.id}</p>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <button onClick={() => handleUniversalPrint('80mm')} className="p-3 border rounded-xl flex flex-col items-center gap-2 hover:bg-gray-50 transition-colors group"><Printer size={24} className="text-gray-400 group-hover:text-blue-700" /><span className="text-xs font-medium text-gray-600">Slip (80mm)</span></button>
              <button onClick={() => handleUniversalPrint('a4')} className="p-3 border rounded-xl flex flex-col items-center gap-2 hover:bg-gray-50 transition-colors group"><FileText size={24} className="text-gray-400 group-hover:text-blue-700" /><span className="text-xs font-medium text-gray-600">A4 Paper</span></button>
            </div>
            <button onClick={() => setCheckoutStep('idle')} className="w-full py-3 bg-gray-900 text-white rounded-xl text-sm font-bold shadow-lg shadow-gray-200 active:scale-95 transition-all">Close</button>
          </div>
        </div>
      )}
      
      {/* Order Detail Modal (Re-added) */}
      <OrderDetailModal />

      <ReceiptTemplate order={printOrder} shopProfile={shopProfile} type={printType} promptPayNumber={promptPayAccounts.find(p => p.id === activePromptPayId)?.number} printWithQR={printWithQR} />
      <ItemOptionModal editingItem={editingItem} setEditingItem={setEditingItem} updateCartItemOptions={updateCartItemOptions} />
      <InventoryEditModal editingProduct={editingProduct} setEditingProduct={setEditingProduct} products={products} setProducts={setProducts} />
      <PinModal isOpen={pinModal.isOpen} title={pinModal.title} onClose={() => setPinModal({ ...pinModal, isOpen: false })} targetPin={pinModal.target === 'settings' ? SETTINGS_PIN : FINANCE_PIN} onSuccess={() => { if (pinModal.target === 'settings') { setView('settings'); } else if (pinModal.target === 'finance') { setFinanceUnlocked(true); } }} />
      <CameraScannerModal isOpen={isScannerOpen} onClose={() => setIsScannerOpen(false)} onScan={handleScan} />
    </div>
  );
}